import React from "react";
import * as M from "../Modal";
import * as H from "../../hooks";
import * as C from "../../Common";
import * as S from "../../services";
import { T, TB, TC, URL } from "../../Constants";
import * as US from "../../services/user.service";

export type UploaderProps = {
    /** The id of the submission linked to the plan */
    submission: { roots: string, type: string };
    /** Callback to validate the file entry for the plan */
    onValidate: (params: ReturnType<T.API.EDL.PLAN.SavePlanFiles> & ReturnType<T.API.EDL.PLAN.GetJsonFromPdf> & { savedData?: T.Plan, submission: { roots: string, type: string } }) => void;
}

export type SavedData = T.Plan;

export const Uploader: React.FC<UploaderProps> = ({ onValidate, ...props }) => {
    const loading = H.useBoolean(false);
    const [pdf_file, set_pdf_file] = React.useState<T.File>(null);
    const [selectedPlan, set_selectedPlan] = React.useState<string>(null);
    const [existingPlans, set_existingPlans, status] = H.useAsyncState<T.Plan[]>([]);

    React.useEffect(() => {
        let is_subscribed = true;

        const api_params = {
            type: "pdf_entity",
            $or: [{ origin: props.submission.roots }, { location: props.submission.roots }]
        };

        US.getPlansFromFilter(api_params)
            .then(res => is_subscribed && set_existingPlans(res.data, "done"))
            .catch(() => is_subscribed && set_existingPlans([], "error"));
        return () => {
            is_subscribed = false;
            set_existingPlans([], "load");
        }
    }, [props.submission.roots, set_existingPlans]);

    const save_plan = React.useCallback(() => {
        const dismount = M.renderLoader();
        const plan = existingPlans.find(plan => plan._id === selectedPlan);
        const plan_url = plan ? URL.APP_DOMAIN + plan.src : pdf_file.url;

        S.getJsonFromPdf({ url: plan_url, context: { roots: props.submission.roots, type: props.submission.type } }).then(json_data => {
            if (plan) {
                dismount();
                onValidate({ ...json_data.data, savedData: plan, pdf: plan.src, submission: { roots: props.submission.roots, type: props.submission.type } });
            }
            else S.savePlanFiles({ submission: props.submission.roots, pdf: pdf_file })
                .then(res => onValidate({ ...res.data, ...json_data.data, submission: { roots: props.submission.roots, type: props.submission.type } }))
                .catch(M.Alerts.loadError)
                .finally(dismount);
        })
            .catch(e => {
                dismount();
                M.Alerts.loadError(e);
            });
    }, [onValidate, props.submission, pdf_file, selectedPlan]);

    const plan_options = React.useMemo(() => existingPlans.map(plan => ({ label: plan.name, value: plan._id })), [existingPlans]);
    const allow_validate = React.useMemo(() => (pdf_file || selectedPlan) && TB.mongoIdValidator(props.submission.roots), [pdf_file, selectedPlan, props.submission.roots,]);

    return <C.Spinner status={status} >
        <div>
            <C.Title level={5} className="card-title" text={TC.GLOBAL_UPLOAD_PLAN} />

            <C.Form.Select
                value={selectedPlan}
                disabled={!!pdf_file}
                options={plan_options}
                onChange={set_selectedPlan}
            />

            <C.Form.FileUploader
                filePattern=".pdf"
                disabled={!!selectedPlan}
                onChange={files => set_pdf_file(files?.[0])}
                label={TC.ANNOTATION_CREATE_PLAN || "Create plan"}
            />

            <C.Button
                onClick={save_plan}
                text={TC.GLOBAL_CONFIRM}
                disabled={!allow_validate}
                icon={{ icon: "save", spin: loading.value, spinIcon: "spinner" }}
            />
        </div>
    </C.Spinner>
}

export default Uploader;
